import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { env } from '@/lib/env';
import { cn } from '@/lib/utils';
import { useAuth } from '@/features/auth/use-auth';
import { activeSectionTitle, NAV_SECTIONS, visibleSections } from './nav-items';
import { useChatNav } from './use-chat-nav';
import { usePendingBadges } from './use-pending-badges';

const CHAT_PATH = '/chat';

interface SidebarNavProps {
  /** Called after a link is chosen — closes the drawer on mobile. */
  onNavigate?: () => void;
}

/** Stable id for the panel a section header controls. */
function panelId(title: string): string {
  return `nav-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
}

/**
 * The navigation tree, shared by the fixed desktop sidebar and the mobile
 * drawer so the two cannot drift apart.
 *
 * Sections collapse, one open at a time. An admin sees fifty-odd links, which
 * as a flat list is a scroll in its own right and hides where you are; as eight
 * groups it fits on a phone without scrolling and the open group tells you.
 */
export function SidebarNav({ onNavigate }: SidebarNavProps) {
  const { canAny } = useAuth();
  const { pathname } = useLocation();
  const chat = useChatNav();
  const pending: Record<string, number> = { ...usePendingBadges() };
  if (chat.unread > 0) pending[CHAT_PATH] = chat.unread;

  // Chat is not a permission — it is a company-wide switch, and one that
  // also closes for a login whose employee has left. The server says which,
  // through the same poll that carries the badge, and the link follows it.
  const sections = visibleSections(NAV_SECTIONS, canAny)
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => item.to !== CHAT_PATH || chat.enabled),
    }))
    .filter((section) => section.items.length > 0);

  const activeTitle = activeSectionTitle(sections, pathname);
  const [openTitle, setOpenTitle] = useState<string | null>(activeTitle);

  // Keyed on the section rather than the path: navigating within a group must
  // not disturb it, but arriving in another group — by link, breadcrumb,
  // redirect or the Back button — should open the one holding the page.
  useEffect(() => {
    if (activeTitle) setOpenTitle(activeTitle);
  }, [activeTitle]);

  return (
    <div className="flex h-full flex-col bg-sidebar text-sidebar-foreground">
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-sidebar-border px-4">
        {/* The mark carries its own colour, so no tinted plate behind it. */}
        <img
          src="/kiobytes-logo.png"
          alt=""
          aria-hidden="true"
          className="size-7 shrink-0 object-contain"
        />
        <span className="truncate font-semibold tracking-tight">{env.VITE_APP_NAME}</span>
      </div>

      <nav aria-label="Main" className="flex-1 overflow-y-auto px-2 py-3">
        {sections.map((section) => {
          const open = section.pinned === true || openTitle === section.title;
          const id = panelId(section.title);

          // Rolled up onto the header so a collapsed group still says there is
          // work waiting behind it — otherwise collapsing would hide the one
          // thing the badges exist to surface.
          const waiting = section.items.reduce((sum, item) => sum + (pending[item.to] ?? 0), 0);

          return (
            <div key={section.title} className="mb-2 last:mb-0">
              {section.pinned ? (
                <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {section.title}
                </p>
              ) : (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={id}
                  onClick={() => setOpenTitle(open ? null : section.title)}
                  className={cn(
                    'flex w-full items-center gap-2 rounded-md px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                    open
                      ? 'text-sidebar-foreground'
                      : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                  )}
                >
                  <ChevronDown
                    className={cn(
                      'size-3.5 shrink-0 transition-transform duration-150',
                      !open && '-rotate-90',
                    )}
                    aria-hidden="true"
                  />
                  <span className="truncate">{section.title}</span>
                  {!open && waiting > 0 ? (
                    <span
                      className="ml-auto min-w-5 rounded-full bg-sidebar-primary px-1.5 py-0.5 text-center text-[11px] font-semibold tabular-nums text-sidebar"
                      aria-label={`${waiting} waiting for you`}
                    >
                      {waiting}
                    </span>
                  ) : null}
                </button>
              )}

              <ul
                id={id}
                hidden={!open}
                className={cn(
                  'space-y-0.5',
                  // A rail rather than indentation alone: it survives the long
                  // labels in Finance, which would otherwise wrap back level
                  // with the header and stop reading as children.
                  !section.pinned && 'ml-4 mt-0.5 border-l border-sidebar-border pl-1.5',
                )}
              >
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      // Only the dashboard needs `end` — without it, "/" would
                      // match every route and stay permanently highlighted.
                      end={item.to === '/'}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        cn(
                          'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                          isActive
                            ? 'bg-sidebar-primary/12 text-sidebar-primary'
                            : 'text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
                        )
                      }
                    >
                      <item.icon className="size-4 shrink-0" aria-hidden="true" />
                      <span className="truncate">{item.label}</span>
                      {item.planned && (
                        <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase text-muted-foreground">
                          Soon
                        </span>
                      )}
                      {pending[item.to] ? (
                        <span
                          className="ml-auto min-w-5 rounded-full bg-sidebar-primary px-1.5 py-0.5 text-center text-[11px] font-semibold tabular-nums text-sidebar"
                          // Read out as words: a bare number announced after a
                          // link name tells a screen-reader user nothing.
                          aria-label={`${pending[item.to]} waiting for you`}
                        >
                          {pending[item.to]}
                        </span>
                      ) : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </nav>
    </div>
  );
}
